import { useState } from "react";
import { Inbox, Brain, Route, Smartphone, Camera, CheckCircle2, FileText, Receipt } from "lucide-react";

const STEPS = [
  { Icon: Inbox, title: "Import / Create", desc: "Pull from 811 feeds or create manually. Auto-classify by type, urgency, customer.", panel: { kind: "intake" } },
  { Icon: Brain, title: "AI Risk Review", desc: "LocateOS scores each ticket for complexity, history, and SLA risk before it's touched.", panel: { kind: "risk" } },
  { Icon: Route, title: "Dispatch & Assign", desc: "Smart routing assigns tickets to the right locator based on location, workload, and skill.", panel: { kind: "dispatch" } },
  { Icon: Smartphone, title: "Field Locate", desc: "Locator receives work on mobile app with map, contact info, and job details.", panel: { kind: "field" } },
  { Icon: Camera, title: "Evidence Capture", desc: "Locator uploads GPS-tagged photos, notes, and completion data from the field.", panel: { kind: "evidence" } },
  { Icon: CheckCircle2, title: "Supervisor Review", desc: "QA review queue flags incomplete closeouts before they leave the system.", panel: { kind: "qa" } },
  { Icon: FileText, title: "Customer Closeout", desc: "Customer-specific reporting rules generate compliant closeout packages automatically.", panel: { kind: "closeout" } },
  { Icon: Receipt, title: "Billing & Reporting", desc: "Production data, payroll hours, and billing-ready records flow to downstream systems.", panel: { kind: "billing" } },
];

function MiniMockup({ kind }) {
  if (kind === "intake") {
    return (
      <div className="space-y-2">
        {["811 Portal · state feed", "Email · georgia.power@", "CSV upload · weekly", "API · custom feed"].map((s, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2 rounded-md bg-[#0a0a0a] border border-white/5 text-xs">
            <span className="text-white">{s}</span>
            <span className="status-pill bg-[#22C55E]/15 text-[#22C55E]">CONNECTED</span>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "risk") {
    return (
      <div className="space-y-3">
        {[
          { id: "LOC-23877", r: 92, c: "bg-[#EF4444]" },
          { id: "LOC-23914", r: 78, c: "bg-[#F97316]" },
          { id: "LOC-23921", r: 42, c: "bg-[#F97316]" },
          { id: "LOC-23905", r: 14, c: "bg-[#22C55E]" },
        ].map((t) => (
          <div key={t.id} className="flex items-center gap-3 text-xs">
            <span className="font-mono text-white w-24">{t.id}</span>
            <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className={`h-full ${t.c}`} style={{ width: `${t.r}%` }} />
            </div>
            <span className="font-mono text-white w-8 text-right">{t.r}</span>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "dispatch") {
    return (
      <div className="space-y-2 text-xs">
        {[
          { l: "Marcus T.", c: 6, ld: "Atlanta · 6 tickets" },
          { l: "Jordan R.", c: 4, ld: "Nashville · 4 tickets" },
          { l: "Sarah K.", c: 8, ld: "Chicago · 8 tickets" },
        ].map((d) => (
          <div key={d.l} className="flex items-center justify-between px-3 py-2 rounded-md bg-[#0a0a0a] border border-white/5">
            <div>
              <div className="text-white font-medium">{d.l}</div>
              <div className="text-[10px] text-[#94A3B8]">{d.ld}</div>
            </div>
            <div className="text-[#F97316] font-mono">{d.c}</div>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "field") {
    return (
      <div className="text-xs space-y-2">
        <div className="px-3 py-2 rounded-md bg-[#0a0a0a] border border-white/5">
          <div className="text-[10px] text-[#94A3B8]">NEXT JOB</div>
          <div className="text-white font-medium">412 Peachtree St · Atlanta GA</div>
          <div className="text-[#F97316] font-mono mt-1">2.4 mi · 9 min</div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {["MAP", "DETAILS", "START"].map((b) => (
            <div key={b} className={`text-center py-2 rounded-md text-[10px] font-bold ${b === "START" ? "bg-[#F97316] text-white" : "bg-white/5 text-white"}`}>{b}</div>
          ))}
        </div>
      </div>
    );
  }
  if (kind === "evidence") {
    return (
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="aspect-square rounded-md bg-gradient-to-br from-[#FB923C]/30 to-[#0a0a0a] border border-white/5 grid place-items-center">
            <Camera size={16} className="text-white/40" />
          </div>
        ))}
      </div>
    );
  }
  if (kind === "qa") {
    return (
      <div className="space-y-2 text-xs">
        {[
          { id: "LOC-23905", st: "READY" },
          { id: "LOC-23901", st: "MISSING PHOTO" },
          { id: "LOC-23899", st: "READY" },
        ].map((q) => (
          <div key={q.id} className="flex items-center justify-between px-3 py-2 rounded-md bg-[#0a0a0a] border border-white/5">
            <span className="font-mono text-white">{q.id}</span>
            <span className={`status-pill ${q.st === "READY" ? "bg-[#22C55E]/15 text-[#22C55E]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>{q.st}</span>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "closeout") {
    return (
      <div className="text-xs space-y-2">
        <div className="px-3 py-2 rounded-md bg-[#0a0a0a] border border-white/5">
          <div className="text-[10px] text-[#94A3B8]">CLOSEOUT PACKAGE · LOC-23905</div>
          <div className="text-white">Georgia Power · Auto-formatted PDF</div>
        </div>
        <div className="flex items-center gap-2 text-[#22C55E] text-[11px]">
          <CheckCircle2 size={12} /> Photos · Notes · GPS · Signature
        </div>
      </div>
    );
  }
  // billing
  return (
    <div className="text-xs space-y-2">
      {[
        { m: "Atlanta", v: "$48,210" },
        { m: "Nashville", v: "$32,580" },
        { m: "Chicago", v: "$61,940" },
      ].map((b) => (
        <div key={b.m} className="flex items-center justify-between px-3 py-2 rounded-md bg-[#0a0a0a] border border-white/5">
          <span className="text-white">{b.m}</span>
          <span className="font-mono text-[#22C55E]">{b.v}</span>
        </div>
      ))}
    </div>
  );
}

export default function LifecycleStepper() {
  const [active, setActive] = useState(0);
  const step = STEPS[active];

  return (
    <section data-testid="lifecycle-section" className="section-y relative">
      <div className="absolute top-1/4 right-0 w-96 h-96 rounded-full bg-[#FB923C] mix-blend-screen blur-[140px] opacity-15 pointer-events-none" />
      <div className="container-x">
        <div className="max-w-3xl reveal mb-12">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#F97316] mb-3">The Solution</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
            One platform for the full ticket lifecycle.
          </h2>
        </div>

        {/* Stepper */}
        <div className="relative reveal">
          <div className="absolute top-7 left-0 right-0 h-px bg-white/10 hidden md:block" />
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2 md:gap-1 relative">
            {STEPS.map((s, i) => {
              const Icon = s.Icon;
              const isActive = i === active;
              return (
                <button
                  key={s.title}
                  data-testid={`lifecycle-step-${i}`}
                  onClick={() => setActive(i)}
                  className="flex flex-col items-center text-center group"
                >
                  <div
                    className={`h-14 w-14 rounded-2xl grid place-items-center border transition-all ${
                      isActive
                        ? "bg-[#F97316] border-[#F97316] shadow-[0_0_24px_rgba(249,115,22,0.55)]"
                        : "bg-[#171717] border-white/10 group-hover:border-white/30"
                    }`}
                  >
                    <Icon size={20} className={isActive ? "text-white" : "text-[#94A3B8] group-hover:text-white"} />
                  </div>
                  <div className={`mt-3 text-[11px] font-medium leading-tight ${isActive ? "text-white" : "text-[#94A3B8]"}`}>
                    {s.title}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Panel */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-6 reveal">
          <div className="rounded-2xl glass p-7">
            <div className="text-xs font-mono uppercase tracking-widest text-[#F97316] mb-2">
              Step {active + 1} / 8
            </div>
            <h3 className="font-display font-bold text-2xl text-white mb-3">{step.title}</h3>
            <p className="text-[#94A3B8] leading-relaxed">{step.desc}</p>

            <div className="mt-5 flex items-center gap-2 text-xs">
              <span className="status-pill bg-[#FB923C]/20 text-[#FDBA74]">CONFIGURABLE</span>
              <span className="status-pill bg-[#22C55E]/15 text-[#22C55E]">AUDITED</span>
            </div>
          </div>

          <div className="rounded-2xl glass p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-white font-medium">{step.title}</div>
              <div className="text-[10px] font-mono text-[#94A3B8]">PREVIEW</div>
            </div>
            <MiniMockup kind={step.panel.kind} />
          </div>
        </div>
      </div>
    </section>
  );
}
